import { useEffect, useRef } from "react";
import Router from "next/router";
import { setHostUrl } from "../services/embedHost";
import {installEmbeddedEventNavigation} from '../services/embedded-events';

/**
 * EmbedBridge — pont iframe ⇄ site parent. Monté UNIQUEMENT en mode embed
 * (props.render === "embed", cf. pages/_app.js).
 *
 * Deux fonctions, dans les deux sens :
 *
 *  1. iframe → parent : la taille RÉELLE de la page est postée à chaque fois
 *     qu'elle change (chargement, images, polices, injections DOM, resize,
 *     navigation interne). Le parent n'a plus à deviner une hauteur : il
 *     applique celle qu'il reçoit à son <iframe> (ou en fait ce qu'il veut).
 *
 *  2. parent → iframe : le parent peut injecter/retirer sa PROPRE feuille de
 *     style dans la page embarquée, pour l'accorder à sa charte. Le CSS est
 *     posé dans un <style> dédié, remis en fin de <head> à chaque application
 *     (donc gagnant à spécificité égale ; `!important` reste conseillé face
 *     aux styles inline des thèmes).
 *
 * Plus une préparation indispensable au point 1 : les hauteurs exprimées en
 * unités viewport sont figées en pixels (cf. freezeViewportUnits) — sans ça,
 * l'auto-resize s'emballe et la page enfle sans jamais se stabiliser.
 *
 * PROTOCOLE
 * ─────────
 * iframe → parent — toujours { source: "kehila-embed", embedId, type, ... } :
 *   { type: "ready",    height, width, path }      au montage
 *   { type: "resize",   height, width }             à chaque changement de taille
 *   { type: "navigate", height, width, path }       après une navigation interne
 *
 * Les messages partent en targetOrigin "*" (cf. `post`) : ils ne portent donc
 * que le CHEMIN, jamais l'URL complète avec sa query string.
 *
 * parent → iframe — { source: "kehila-embed-host", type, ... } :
 *   { type: "css", css: "<css>" }  injecte / remplace la feuille du parent
 *   { type: "css", css: null }     la retire
 *   { type: "measure" }            force un renvoi immédiat de la taille
 *   { type: "location", url }      l'adresse de SA page — d'où le donateur vient,
 *                                  et où il doit revenir après avoir payé
 *
 * `embedId` : repris tel quel du paramètre d'URL `?embedId=` s'il est présent.
 * Sert au parent à router les messages quand il embarque PLUSIEURS iframes
 * (sinon il compare `event.source` à `iframe.contentWindow`).
 *
 * SÉCURITÉ. Le CSS du parent est purement cosmétique (aucun script), mais il
 * peut masquer du contenu : deux garde-fous, pilotés par la config de l'org
 * (admin → Configuration du site → Intégration) et transmis via `embed` :
 *   - `allowCustomCss: false` → toute injection est ignorée ;
 *   - `allowedOrigins: [...]` → seuls ces sites parents sont écoutés (et sont
 *     les seules cibles de nos propres messages). Liste vide = tout parent.
 */

// Un CSS d'habillage réaliste tient largement là-dedans ; au-delà, on est dans
// l'anomalie (ou l'abus) et on ignore plutôt que de faire ramer la page.
const MAX_CSS_LENGTH = 200000;

const STYLE_ID = "kehila-embed-custom-css";

// Unités de hauteur relatives au viewport, à figer en embed (cf. freezeViewportUnits).
const VIEWPORT_UNIT_RE = /(-?\d+(?:\.\d+)?)(svh|lvh|dvh|vmin|vmax|vh)\b/gi;

/**
 * Combien de pixels vaut 1vh dans une page embarquée.
 *
 * PAS `window.innerHeight` : dans une iframe auto-redimensionnée, c'est la
 * hauteur que le parent vient de nous donner — donc la hauteur de la page
 * elle-même. On prend la hauteur de l'ÉCRAN du visiteur, bornée : elle ne
 * dépend pas de l'iframe, et donne un rendu proche de ce qu'il verrait sur le
 * site en direct (un hero « 62vh » reste un grand visuel, pas un mur de 5000px).
 */
function viewportUnitBasis() {
    const screenHeight = (typeof screen !== "undefined" && screen.availHeight) || 800;
    return Math.max(560, Math.min(screenHeight, 1000)) / 100;
}

/**
 * Fige les hauteurs exprimées en unités viewport (vh, svh, dvh, vmin, vmax) en
 * pixels, dans TOUTES les feuilles de style de la page embarquée.
 *
 * POURQUOI (le piège central de l'auto-resize) : le parent applique la hauteur
 * qu'on lui envoie à l'iframe → le viewport de l'iframe devient aussi haut que
 * la page → un bloc en `62vh` grandit → la page grandit → on renvoie une
 * hauteur plus grande… La boucle ne converge jamais et la page enfle jusqu'à
 * l'absurde. Aucune ruse de mesure ne peut la casser : il faut supprimer la
 * dépendance au viewport, comme le fait déjà `stripViewportUnits` (aiI18n.js)
 * pour le HTML des blocs IA.
 *
 * `vw` est conservé : la LARGEUR de l'iframe est imposée par le parent et ne
 * dépend pas de notre contenu — aucune boucle possible de ce côté.
 *
 * N'est appelé qu'en mode embed : le site classique n'est jamais touché.
 */
const frozenSheets = typeof WeakSet !== "undefined" ? new WeakSet() : null;

function freezeViewportUnits() {
    const basis = viewportUnitBasis();

    const freezeRules = (container) => {
        let rules;
        try {
            rules = container.cssRules;
        } catch (e) {
            // Feuille d'une autre origine (Google Fonts…) : illisible, et sans
            // hauteur viewport de toute façon.
            return;
        }
        if (!rules) return;

        for (const rule of rules) {
            // ⚠️ Ne PAS tester `rule.cssRules` en premier pour distinguer un
            // conteneur d'une règle de style : depuis le CSS nesting, Chrome
            // expose `cssRules` (vide) sur TOUTE règle de style. On regarde donc
            // les déclarations d'abord, puis on descend dans les règles filles —
            // ce qui couvre @media/@supports/@layer ET le nesting.
            const style = rule.style;

            if (rule.cssRules && rule.cssRules.length) {
                freezeRules(rule);
            }

            if (!style || !style.length) continue;

            for (let i = 0; i < style.length; i++) {
                const prop = style[i];
                const value = style.getPropertyValue(prop);
                if (!value || value.indexOf("v") === -1) continue;

                VIEWPORT_UNIT_RE.lastIndex = 0;
                if (!VIEWPORT_UNIT_RE.test(value)) continue;

                VIEWPORT_UNIT_RE.lastIndex = 0;
                const frozen = value.replace(
                    VIEWPORT_UNIT_RE,
                    (_, n) => Math.round(parseFloat(n) * basis) + "px"
                );
                try {
                    style.setProperty(prop, frozen, style.getPropertyPriority(prop));
                } catch (e) { /* règle en lecture seule : on passe */ }
            }
        }
    };

    for (const sheet of document.styleSheets) {
        if (frozenSheets && frozenSheets.has(sheet)) continue;

        let rules = null;
        try {
            rules = sheet.cssRules;
        } catch (e) {
            // Autre origine : illisible, mais définitivement — inutile d'y revenir.
            if (frozenSheets) frozenSheets.add(sheet);
            continue;
        }

        // Feuille encore VIDE = <link> déjà dans le <head> mais pas encore
        // téléchargé. La marquer traitée maintenant, c'est ne jamais figer ses
        // règles quand elles arriveront (et c'est exactement le cas du CSS des
        // thèmes, chargé par next/head) : on repasse au prochain tour.
        if (!rules || rules.length === 0) continue;

        if (frozenSheets) frozenSheets.add(sheet);
        freezeRules(sheet);
    }

    freezeInlineViewportUnits(basis);
}

/**
 * Même gel, mais sur les `style="…"` INLINE — que le CSSOM des feuilles ne
 * contient pas. Les thèmes en posent partout (`style={{minHeight:"50vh"}}`), et
 * ceux qui ne sont pas déjà neutralisés en embed rejouent exactement la boucle
 * d'emballement : `LayoutError` (100vh, facteur 1 → divergence), l'aperçu PDF
 * du WebEditor (70vh, deux blocs suffisent), l'espace compte (60vh), une
 * quinzaine de pages Ummah/Travel (50vh → la page double et oscille).
 *
 * Le sélecteur d'attribut restreint le balayage aux seuls éléments concernés,
 * et la réécriture est idempotente (une valeur en px ne contient plus d'unité
 * viewport) — on peut donc repasser aussi souvent que nécessaire.
 */
function freezeInlineViewportUnits(basis) {
    let nodes;
    try {
        nodes = document.querySelectorAll('[style*="vh"],[style*="vmin"],[style*="vmax"]');
    } catch (e) {
        return;
    }

    for (const el of nodes) {
        const value = el.getAttribute("style");
        if (!value) continue;

        VIEWPORT_UNIT_RE.lastIndex = 0;
        if (!VIEWPORT_UNIT_RE.test(value)) continue;

        VIEWPORT_UNIT_RE.lastIndex = 0;
        el.setAttribute(
            "style",
            value.replace(VIEWPORT_UNIT_RE, (_, n) => Math.round(parseFloat(n) * basis) + "px")
        );
    }
}

function normalizeOrigin(value) {
    if (typeof value !== "string") return null;
    const trimmed = value.trim().toLowerCase().replace(/\/+$/, "");
    return trimmed || null;
}

function measure() {
    const body = document.body;
    const de = document.documentElement;
    if (!body || !de) return { height: 0, width: 0 };

    // ⚠️ On mesure le BODY, jamais `documentElement` : dans une iframe, <html>
    // fait au moins la hauteur du viewport, donc au moins la hauteur que le
    // parent vient d'appliquer → la page ne pourrait plus jamais RÉTRÉCIR
    // (passer d'une page longue à une page courte laisserait un grand vide).
    let height = Math.max(body.scrollHeight, body.offsetHeight);

    // Enfants directs : rattrape ce que scrollHeight ignore — la marge basse du
    // dernier bloc (margin collapsing) et un élément positionné qui dépasse.
    // Les éléments `fixed` sont exclus : ils suivent le viewport, pas le flux,
    // et feraient grandir la mesure à chaque agrandissement de l'iframe.
    for (const child of body.children) {
        let style;
        try {
            style = getComputedStyle(child);
        } catch (e) {
            continue;
        }
        if (!style || style.position === "fixed" || style.display === "none") continue;

        const rect = child.getBoundingClientRect();
        const marginBottom = parseFloat(style.marginBottom) || 0;
        height = Math.max(height, rect.bottom + window.scrollY + marginBottom);
    }

    const width = Math.max(body.scrollWidth, de.clientWidth);

    return { height: Math.ceil(height), width: Math.ceil(width) };
}

export default function EmbedBridge({ embed, eventContext }) {
    useEffect(()=>{
        if (typeof window==='undefined' || window.parent===window || !eventContext?.org?.BASE_WEB_URL) return;
        return installEmbeddedEventNavigation(eventContext,window);
    },[eventContext?.org?.BASE_WEB_URL,eventContext?.route?.join('/'),eventContext?.render]);
    // Dernière taille postée : sert à ne pas inonder le parent de messages
    // identiques (le MutationObserver se déclenche à la moindre broutille).
    const lastSize = useRef({ height: 0, width: 0 });

    // La politique d'embed arrive dans un OBJET recréé à chaque page : dépendre
    // de sa référence relancerait tout le pont (observers, listeners, "ready")
    // à chaque navigation interne. On dépend de son CONTENU, qui lui ne change
    // que si l'admin modifie la configuration.
    const embedRef = useRef(embed);
    embedRef.current = embed;
    const embedKey = JSON.stringify(embed || {});

    useEffect(() => {
        const embed = embedRef.current;
        if (typeof window === "undefined") return;
        // Pas dans une iframe → personne à qui parler.
        if (window.parent === window) return;

        const allowedOrigins = (Array.isArray(embed?.allowedOrigins) ? embed.allowedOrigins : [])
            .map(normalizeOrigin)
            .filter(Boolean);
        // `restrictOrigins` est posé par le serveur dès que l'admin a SAISI
        // quelque chose, même si rien n'a survécu à la validation : une saisie
        // fautive doit fermer la porte, pas la laisser grande ouverte.
        const restrictOrigins = embed?.restrictOrigins === true || allowedOrigins.length > 0;
        const allowCustomCss = embed?.allowCustomCss !== false;

        const embedId = (() => {
            try {
                return new URLSearchParams(window.location.search).get("embedId") || null;
            } catch (e) {
                return null;
            }
        })();

        // Le premier handshake annonce uniquement la taille et l'identifiant.
        // Après validation du parent direct et de son origine, les messages
        // suivants sont adressés à cette origine exacte.
        let parentOrigin = null;
        function post(type, payload = {}) {
            const message = { source: "kehila-embed", embedId, type, ...payload };
            try {
                window.parent.postMessage(message, parentOrigin || "*");
            } catch (e) { /* parent parti : on ignore */ }
        }

        // "ready" parti avec une hauteur nulle (page pas encore rendue) : à
        // rejouer dès qu'on a une vraie mesure.
        let readyNeedsReplay = false;

        function postSize(type = "resize", extra = {}) {
            const { height, width } = measure();

            // Une hauteur nulle n'est JAMAIS une vraie mesure : c'est une page
            // dont le navigateur a suspendu le layout (onglet en arrière-plan,
            // iframe encore masquée par le parent). La poster comme taille
            // ferait replier l'iframe à zéro.
            // MAIS on laisse toujours passer "ready" : c'est le signal qui dit
            // au parent qu'il peut envoyer son CSS, il n'est émis qu'une fois,
            // et une intégration dans un onglet/accordéon replié mesure 0 au
            // montage — l'avaler priverait définitivement le client de sa
            // personnalisation. Le parent doit ignorer une hauteur nulle (le
            // snippet fourni le fait déjà) ; par sécurité on rejoue "ready" à
            // la première mesure réelle.
            if (height <= 0) {
                if (type !== "ready") return;
                readyNeedsReplay = true;
                post(type, { height, width, ...extra });
                return;
            }

            if (readyNeedsReplay) {
                readyNeedsReplay = false;
                post("ready", { height, width, path: window.location.pathname });
            }

            // Seuil de 2px : évite l'aller-retour infini quand le parent
            // applique la hauteur reçue et que l'arrondi rejoue une mesure.
            if (
                type === "resize" &&
                Math.abs(height - lastSize.current.height) < 2 &&
                Math.abs(width - lastSize.current.width) < 2
            ) return;

            lastSize.current = { height, width };
            post(type, { height, width, ...extra });
        }

        let rafId = null;
        let timeoutId = null;
        function schedule() {
            // rAF pour se caler sur la peinture, ET setTimeout de secours :
            // rAF est GELÉ quand l'iframe est masquée ou l'onglet en fond,
            // et sans ce filet aucune mesure ne partirait plus.
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => postSize());
            if (timeoutId) clearTimeout(timeoutId);
            timeoutId = setTimeout(() => postSize(), 0);
        }

        // Les hauteurs en unités viewport sont figées AVANT la première mesure :
        // sans ça, la toute première hauteur envoyée est déjà calculée sur la
        // hauteur provisoire de l'iframe (cf. freezeViewportUnits).
        freezeViewportUnits();

        // ---- iframe → parent -------------------------------------------------
        window.addEventListener("load", schedule);
        window.addEventListener("resize", schedule);

        // <html> ET <body> : <html> bouge même quand body garde sa taille mais
        // qu'un enfant déborde.
        let resizeObserver = null;
        if (typeof ResizeObserver !== "undefined") {
            resizeObserver = new ResizeObserver(schedule);
            resizeObserver.observe(document.documentElement);
            resizeObserver.observe(document.body);
        }

        // Rattrape les injections DOM tardives (accordéons, contenus fetchés
        // côté client — galeries, événements, actualités…).
        //
        // Et surtout : les thèmes rendent leurs `<style>` DANS l'arbre React,
        // donc dans le <body> — le headObserver ci-dessous ne les voit jamais.
        // Sans ce test, une navigation interne vers une page qui apporte de
        // nouvelles règles en `vh` laissait l'iframe s'emballer visiblement
        // jusqu'au prochain tour de l'intervalle de secours.
        const mutationObserver = new MutationObserver((records) => {
            for (const record of records) {
                for (const node of record.addedNodes) {
                    if (node.nodeName === "STYLE" || node.nodeName === "LINK") {
                        freezeViewportUnits();
                        break;
                    }
                }
            }
            schedule();
        });
        mutationObserver.observe(document.body, { childList: true, subtree: true, attributes: true });

        // Les thèmes injectent leurs styles au fil du rendu (styled-jsx, next/head,
        // chunks chargés à la demande) : chaque nouvelle feuille doit être figée à
        // son tour, sinon un `vh` arrivé après coup relance la boucle.
        const headObserver = new MutationObserver(() => {
            freezeViewportUnits();
            schedule();
        });
        headObserver.observe(document.head, { childList: true });

        // Un <link rel="stylesheet"> est ajouté au <head> AVANT d'être téléchargé :
        // au moment de la mutation ses règles n'existent pas encore. On refige donc
        // à son `load` (qui ne bulle pas → écoute en phase de capture).
        const onStyleLoad = (event) => {
            const target = event.target;
            if (target && (target.tagName === "LINK" || target.tagName === "STYLE")) {
                freezeViewportUnits();
                schedule();
            }
        };
        document.addEventListener("load", onStyleLoad, true);

        // Les polices web changent les hauteurs de ligne une fois appliquées.
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(schedule).catch(() => {});
        }

        // Chaque média qui finit de charger décale le bas de page.
        function watchMedia() {
            const medias = document.querySelectorAll("img, iframe, video");
            for (const m of medias) {
                if (m.tagName === "IMG" && m.complete) continue;
                m.addEventListener("load", schedule, { once: true });
                m.addEventListener("error", schedule, { once: true });
            }
        }
        watchMedia();

        // Filet permanent : couvre les cas où aucun observer ne se déclenche
        // (transition CSS lente, iframe rendue visible par le parent…).
        const interval = setInterval(() => {
            freezeViewportUnits();
            postSize();
        }, 2000);

        // Navigation interne (SPA) : le parent veut souvent remonter en haut
        // de SON scroll, en plus de la nouvelle hauteur.
        const onRouteChange = () => {
            watchMedia();
            postSize("navigate", { path: window.location.pathname });
            schedule();
        };
        Router.events.on("routeChangeComplete", onRouteChange);

        // ---- parent → iframe -------------------------------------------------
        function applyCss(css) {
            let el = document.getElementById(STYLE_ID);

            if (css == null || css === "") {
                if (el) el.remove();
                schedule();
                return;
            }

            if (!el) {
                el = document.createElement("style");
                el.id = STYLE_ID;
                el.setAttribute("data-kehila-embed", "host-css");
            }
            // textContent (jamais innerHTML) : le CSS ne peut pas s'échapper du
            // <style> ni injecter de balise.
            el.textContent = css;
            // Ré-append systématique → la feuille du parent reste la dernière
            // du <head>, donc gagnante à spécificité égale.
            document.head.appendChild(el);
            schedule();
        }

        const onMessage = (event) => {
            const data = event.data;
            if (!data || typeof data !== "object") return;
            if (data.source !== "kehila-embed-host") return;

            // SEUL le parent direct pilote cette page. `data.source` ne prouve
            // rien : n'importe quelle frame de la page hôte (widget de chat,
            // régie publicitaire, autre iframe) atteint notre window via
            // `top.frames[i]` et pourrait sinon injecter son CSS — masquer le
            // bouton de don, superposer un faux message, déclencher une requête
            // par une url(). Le snippet légitime poste bien depuis le parent.
            if (event.source !== window.parent) return;
            if (data.embedId && embedId && data.embedId !== embedId) return;

            // Liste blanche d'origines : configurée = on la fait respecter ;
            // configurée mais entièrement invalide (`restrictOrigins` sans
            // origine retenue) = on ferme, jamais on n'ouvre.
            if (restrictOrigins) {
                const origin = normalizeOrigin(event.origin);
                if (!origin || !allowedOrigins.includes(origin)) return;
            }
            parentOrigin = normalizeOrigin(event.origin);

            switch (data.type) {
                case "css":
                    if (!allowCustomCss) return;
                    if (data.css == null) return applyCss(null);
                    if (typeof data.css !== "string") return;
                    if (data.css.length > MAX_CSS_LENGTH) return;
                    applyCss(data.css);
                    break;
                case "measure":
                    lastSize.current = { height: 0, width: 0 }; // force le renvoi
                    postSize();
                    break;
                case "location":
                    // L'adresse de la page hôte. Le tunnel de paiement s'en sert
                    // comme adresse de retour : sans elle, le donateur revient
                    // sur NOTRE page au lieu de celle du client où il donnait.
                    // La validation (http(s) seulement) est dans embedHost.
                    setHostUrl(data.url, parentOrigin);
                    break;
                default:
                    break;
            }
        };
        window.addEventListener("message", onMessage);

        // Premier signal : le parent sait qu'il peut envoyer son CSS.
        postSize("ready", { path: window.location.pathname });

        return () => {
            window.removeEventListener("load", schedule);
            window.removeEventListener("resize", schedule);
            window.removeEventListener("message", onMessage);
            document.removeEventListener("load", onStyleLoad, true);
            Router.events.off("routeChangeComplete", onRouteChange);
            if (resizeObserver) resizeObserver.disconnect();
            mutationObserver.disconnect();
            headObserver.disconnect();
            clearInterval(interval);
            if (rafId) cancelAnimationFrame(rafId);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [embedKey]);

    return null;
}
